/**
 * Login / registration screen — passkey-based via WebAuthn.
 *
 * Flow:
 *  - User types a username
 *  - "Sign in with passkey" → /auth/login/start → navigator.credentials.get
 *    → /auth/login/finish
 *  - "Create account" → /auth/register/start → navigator.credentials.create
 *    → /auth/register/finish
 *
 * In SUPPRESS_AUTH mode the worker short-circuits these endpoints and just
 * mints a dev session; the same UI works in both modes.
 */

import { useMemo, useState } from "react";
import { startAuthentication, startRegistration } from "@simplewebauthn/browser";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { randomTagline } from "../taglines/taglines";
import { useToast } from "./toast";

export function LoginScreen({ error }: { error?: string }) {
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [mode, setMode] = useState<"login" | "register">("login");
  const [working, setWorking] = useState(false);
  const [localError, setLocalError] = useState<string | null>(error ?? null);
  const tagline = useMemo(() => randomTagline(), []);
  const qc = useQueryClient();
  const toast = useToast();

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLocalError(null);
    setWorking(true);
    try {
      if (mode === "login") await doLogin();
      else await doRegister();
      qc.invalidateQueries({ queryKey: ["me"] });
      toast.push("Welcome", "success");
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setLocalError(msg);
    } finally {
      setWorking(false);
    }
  }

  async function doLogin() {
    const start = await api.post<{
      handle?: string;
      options?: unknown;
      suppressed?: boolean;
    }>("/auth/login/start", { username: username || undefined });
    if (start.suppressed) {
      await api.post("/auth/login/finish", { handle: "x", response: {} });
      return;
    }
    if (!start.handle || !start.options) throw new Error("malformed login response");
    const response = await startAuthentication({ optionsJSON: start.options as never });
    await api.post("/auth/login/finish", { handle: start.handle, response });
  }

  async function doRegister() {
    if (username.length < 3) throw new Error("username must be at least 3 characters");
    const start = await api.post<{
      handle?: string;
      options?: unknown;
      suppressed?: boolean;
    }>("/auth/register/start", {
      username,
      display_name: displayName || undefined,
    });
    if (start.suppressed) {
      await api.post("/auth/register/finish", { handle: "x", response: {} });
      return;
    }
    if (!start.handle || !start.options) throw new Error("malformed register response");
    const response = await startRegistration({ optionsJSON: start.options as never });
    await api.post("/auth/register/finish", { handle: start.handle, response });
  }

  return (
    <div
      className="min-h-full flex items-center justify-center p-4"
      style={{ background: "var(--bg-alt)" }}
    >
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm rounded-lg shadow-sm border p-6 flex flex-col gap-3"
        style={{ background: "var(--bg-elevated)", borderColor: "var(--border)" }}
      >
        <div className="flex flex-col items-center gap-1 mb-2">
          <span
            aria-hidden
            className="inline-flex w-12 h-12 rounded-xl bg-amber-400 text-black items-center justify-center text-2xl font-black"
          >
            k
          </span>
          <h1 className="text-xl font-bold tracking-tight">keep</h1>
          <p className="text-xs italic" style={{ color: "var(--fg-muted)" }}>
            {tagline}
          </p>
        </div>

        <div className="flex" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={mode === "login"}
            onClick={() => setMode("login")}
            className={`flex-1 py-2 text-sm border-b-2 ${
              mode === "login"
                ? "border-amber-400 font-semibold"
                : "border-transparent text-gray-500"
            }`}
          >
            Sign in
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === "register"}
            onClick={() => setMode("register")}
            className={`flex-1 py-2 text-sm border-b-2 ${
              mode === "register"
                ? "border-amber-400 font-semibold"
                : "border-transparent text-gray-500"
            }`}
          >
            Create account
          </button>
        </div>

        <label className="flex flex-col gap-1">
          <span className="text-xs uppercase tracking-wide" style={{ color: "var(--fg-muted)" }}>
            Username
          </span>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder={mode === "login" ? "username (optional)" : "your-handle"}
            autoComplete="username"
            className="px-3 py-2 rounded border"
            style={{ borderColor: "var(--border)", background: "var(--bg)" }}
          />
        </label>

        {mode === "register" && (
          <label className="flex flex-col gap-1">
            <span className="text-xs uppercase tracking-wide" style={{ color: "var(--fg-muted)" }}>
              Display name
            </span>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="How should we greet you?"
              className="px-3 py-2 rounded border"
              style={{ borderColor: "var(--border)", background: "var(--bg)" }}
            />
          </label>
        )}

        {localError && (
          <div className="text-xs px-2 py-1.5 rounded bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-900">
            {localError}
          </div>
        )}

        <button
          type="submit"
          disabled={working}
          className="mt-2 py-2 px-3 rounded bg-amber-400 text-black font-medium hover:bg-amber-300 disabled:opacity-60"
        >
          {working
            ? "Working…"
            : mode === "login"
              ? "Sign in with passkey"
              : "Create account with passkey"}
        </button>

        <p className="text-xs text-center" style={{ color: "var(--fg-muted)" }}>
          We never store a password. Passkeys live in your device or password manager (iCloud
          Keychain, 1Password, etc.).
        </p>
      </form>
    </div>
  );
}
